import React, { useCallback, useEffect, useRef, useState } from "react";
import "./ContextPage.css";
import { apiGet, apiPost, apiPut, downloadFile } from "../apiClient";
import { VIEW_SCOPE_WORKSPACE } from "../domainCatalog";


const FONT_SIZES = {
  "1": "12px",
  "2": "14px",
  "3": "16px",
  "4": "18px",
  "5": "24px",
  "6": "32px"
};

const ContextPage = ({ token, model, scope = "workstream", onUsageChange }) => {
  const fileInputRef = useRef(null);
  const editorRef = useRef(null);
  const [documentText, setDocumentText] = useState("");
  const [documentName, setDocumentName] = useState("");
  const [savedText, setSavedText] = useState("");
  const [instruction, setInstruction] = useState("");
  const [chatMessages, setChatMessages] = useState([]);
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [assistantLoading, setAssistantLoading] = useState(false);

  const hasUnsavedChanges = documentText !== savedText;
  const hasDocumentContent = htmlToPlainText(documentText).trim().length > 0;
  const workspaceView = scope === VIEW_SCOPE_WORKSPACE;

  const loadContext = useCallback(async () => {
    try {
      const data = await apiGet(`/context${scope === VIEW_SCOPE_WORKSPACE ? "?scope=workspace" : ""}`, { token });
      if (data) {
        const editableDocument = toEditableHtml(data.raw_text || "");
        setDocumentText(editableDocument);
        setSavedText(editableDocument);
        setDocumentName(data.document_name || "Requirements document");
      }
    } catch {
      setMessage("Error loading the requirements document.");
    }
  }, [token, scope]);

  useEffect(() => {
    loadContext();
  }, [loadContext]);

  useEffect(() => {
    if (editorRef.current && editorRef.current.innerHTML !== documentText) {
      editorRef.current.innerHTML = documentText;
    }
  }, [documentText]);

  const uploadDocument = async (file) => {
    if (!file) return;
    setLoading(true);
    setMessage("Uploading and preparing the document...");

    try {
      const formData = new FormData();
      formData.append("file", file);
      const data = await apiPost("/context/upload", formData, { token, model });

      const editableDocument = toEditableHtml(data.raw_text);
      setDocumentText(editableDocument);
      setSavedText(editableDocument);
      setDocumentName(data.document_name || file.name);
      setChatMessages([]);
      setMessage("Document uploaded and saved as the latest version.");
    } catch (error) {
      setMessage(error.message || "Error uploading the document.");
    } finally {
      setLoading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const saveDocument = async () => {
    if (!hasDocumentContent) {
      setMessage("The requirements document cannot be empty.");
      return;
    }

    setLoading(true);
    setMessage("Saving the latest version...");
    try {
      const data = await apiPut("/context", {
        raw_text: documentText,
        document_name: documentName || "Requirements document"
      }, { token, model });

      const editableDocument = toEditableHtml(data.raw_text);
      setDocumentText(editableDocument);
      setSavedText(editableDocument);
      setDocumentName(data.document_name || documentName);
      setMessage("Latest version saved successfully.");
    } catch (error) {
      setMessage(error.message || "Error saving the document.");
    } finally {
      setLoading(false);
    }
  };

  const askAssistant = async () => {
    if (!instruction.trim() || !hasDocumentContent) return;

    const userInstruction = instruction.trim();
    setInstruction("");
    setAssistantLoading(true);
    setChatMessages((current) => [...current, { role: "user", content: userInstruction }]);

    try {
      const data = await apiPost("/context/edit", {
        raw_text: documentText,
        instruction: userInstruction
      }, { token, model });

      setDocumentText(toEditableHtml(data.revised_document));
      setChatMessages((current) => [...current, { role: "assistant", content: data.message }]);
      if (data.revised_document !== documentText) {
        setMessage("Assistant changes applied as a draft. Review them and click Save latest version.");
      }
      onUsageChange?.();
    } catch (error) {
      setChatMessages((current) => [...current, { role: "assistant", content: error.message }]);
    } finally {
      setAssistantLoading(false);
    }
  };

  const syncEditorContent = () => {
    setDocumentText(editorRef.current?.innerHTML || "");
  };

  const formatSelection = (command, value = null) => {
    editorRef.current?.focus();
    document.execCommand(command, false, value);
    syncEditorContent();
  };

  const formatFontSize = (size) => {
    formatSelection("fontSize", size);
    editorRef.current?.querySelectorAll(`font[size="${size}"]`).forEach((element) => {
      element.removeAttribute("size");
      element.style.fontSize = FONT_SIZES[size];
    });
    syncEditorContent();
  };

  const downloadDocument = async (format) => {
    if (hasUnsavedChanges) {
      setMessage("Save the latest version before downloading so the export includes your current changes.");
      return;
    }

    setLoading(true);
    setMessage(`Preparing ${format.toUpperCase()} download...`);
    try {
      await downloadFile(`/context/export/${format}`, {
        token,
        filename: `requirements-document.${format}`
      });
      setMessage(`${format.toUpperCase()} download ready.`);
    } catch (error) {
      setMessage(error.message || `Error downloading ${format.toUpperCase()}.`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="context-page">
      <div className="context-header">
        <div>
          <h1>{workspaceView ? "Workspace requirements" : "Project requirements"}</h1>
          <p>{workspaceView ? "Consolidated requirements from all workstreams." : "The saved latest version is used as context throughout Taskit."}</p>
        </div>
        <div className="context-actions">
          <input
            ref={fileInputRef}
            type="file"
            accept=".txt,.md,.pdf,.docx"
            hidden
            onChange={(event) => uploadDocument(event.target.files?.[0])}
          />
          <button onClick={() => fileInputRef.current?.click()} disabled={loading || workspaceView}>
            {documentText ? "Replace document" : "Upload document"}
          </button>
          <button onClick={() => downloadDocument("docx")} disabled={loading || workspaceView || !hasDocumentContent}>
            Download DOCX
          </button>
          <button onClick={() => downloadDocument("pdf")} disabled={loading || workspaceView || !hasDocumentContent}>
            Download PDF
          </button>
          <button
            className="primary"
            onClick={saveDocument}
            disabled={loading || workspaceView || !hasDocumentContent || !hasUnsavedChanges}
          >
            {loading ? "Working..." : "Save latest version"}
          </button>
        </div>
      </div>

      <div className={`context-status ${hasUnsavedChanges ? "unsaved" : ""}`}>
        <strong>{documentName || "No document uploaded"}</strong>
        <span>{hasUnsavedChanges ? "Unsaved draft" : documentText ? "Latest version saved" : "Upload TXT, MD, PDF, or DOCX"}</span>
      </div>

      {message && <div className="context-message">{message}</div>}

      <div className="context-workspace">
        <section className="context-editor-panel">
          <div className="context-panel-title">
            <h2>{workspaceView ? "Consolidated document" : "Editable document"}</h2>
            <span>{htmlToPlainText(documentText).length.toLocaleString()} characters</span>
          </div>
          <div className="context-format-toolbar" aria-label="Document formatting">
            <button
              type="button"
              aria-label="Bold"
              title="Bold"
              disabled={workspaceView}
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => formatSelection("bold")}
            >
              <strong>B</strong>
            </button>
            <button
              type="button"
              aria-label="Italic"
              title="Italic"
              disabled={workspaceView}
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => formatSelection("italic")}
            >
              <em>I</em>
            </button>
            <select
              aria-label="Font family"
              disabled={workspaceView}
              defaultValue="Arial"
              onChange={(event) => formatSelection("fontName", event.target.value)}
            >
              <option value="Arial">Arial</option>
              <option value="Georgia">Georgia</option>
              <option value="Verdana">Verdana</option>
              <option value="Courier New">Courier New</option>
              <option value="Times New Roman">Times New Roman</option>
            </select>
            <select
              aria-label="Font size"
              disabled={workspaceView}
              defaultValue="3"
              onChange={(event) => formatFontSize(event.target.value)}
            >
              <option value="1">12</option>
              <option value="2">14</option>
              <option value="3">16</option>
              <option value="4">18</option>
              <option value="5">24</option>
              <option value="6">32</option>
            </select>
          </div>
          <div
            ref={editorRef}
            className="context-rich-editor"
            contentEditable={!workspaceView}
            suppressContentEditableWarning
            onInput={syncEditorContent}
            dir="ltr"
            data-placeholder="Upload a requirements document or start writing here..."
            aria-label="Requirements document editor"
          />
        </section>

        <aside className="context-assistant-panel">
          <div className="context-panel-title">
            <h2>Editing assistant</h2>
          </div>
          <div className="context-chat">
            {!chatMessages.length && (
              <p className="context-chat-empty">
                Ask the assistant to clarify, reorganize, expand, or rewrite requirements. Changes remain a draft until you save.
              </p>
            )}
            {chatMessages.map((chatMessage, index) => (
              <div key={index} className={`context-chat-message ${chatMessage.role}`}>
                {chatMessage.content}
              </div>
            ))}
            {assistantLoading && <p className="context-chat-empty">Revising the document...</p>}
          </div>
          <div className="context-assistant-composer">
            <textarea
              className="context-assistant-input"
              value={instruction}
              onChange={(event) => setInstruction(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Enter" && !event.shiftKey) {
                  event.preventDefault();
                  askAssistant();
                }
              }}
              placeholder="Ask a question or request a document change..."
            />
            <button
              className="context-assistant-button"
              onClick={askAssistant}
              disabled={assistantLoading || workspaceView || !instruction.trim() || !hasDocumentContent}
            >
              Send to assistant
            </button>
          </div>
        </aside>
      </div>
    </div>
  );
};

const toEditableHtml = (value) => {
  if (!value) return "";
  if (/<[a-z][\s\S]*>/i.test(value)) return sanitizeHtml(value);
  return sanitizeHtml(value
    .split(/\n{2,}/)
    .map((paragraph) => `<p>${escapeHtml(paragraph).replace(/\n/g, "<br>")}</p>`)
    .join(""));
};

const escapeHtml = (value) => value
  .replace(/&/g, "&amp;")
  .replace(/</g, "&lt;")
  .replace(/>/g, "&gt;")
  .replace(/"/g, "&quot;")
  .replace(/'/g, "&#039;");

const htmlToPlainText = (value) => {
  const element = document.createElement("div");
  element.innerHTML = value;
  return element.textContent || "";
};

const sanitizeHtml = (value) => {
  const template = document.createElement("template");
  template.innerHTML = value;
  template.content.querySelectorAll("script, style, iframe, object, embed").forEach((element) => element.remove());
  template.content.querySelectorAll("*").forEach((element) => {
    [...element.attributes].forEach((attribute) => {
      const name = attribute.name.toLowerCase();
      const isUnsafeUrl = /^java\s*script:/i.test(attribute.value.trim());
      if (name.startsWith("on") || isUnsafeUrl) {
        element.removeAttribute(attribute.name);
      }
    });
  });
  return template.innerHTML;
};

export default ContextPage;
